// Shared modal overlay. Fixed inset:0 (NEVER width:100vw — the platform
// fullscreen-overlay rule) with body scroll locked while open.

import { useEffect } from 'react';

interface Props {
  title: string;
  onClose: () => void;
  maxWidth?: number;
  children: React.ReactNode;
}

export function Modal({ title, onClose, maxWidth = 640, children }: Props) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="qc-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8, 20, 38, 0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '6vh 24px 24px',
        overflowY: 'auto',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: 'var(--brand-card-bg, white)',
          borderRadius: 8,
          boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
          width: '100%',
          maxWidth,
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderBottom: '1px solid var(--brand-border, #D6E4EE)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--brand-text, #001C3E)',
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: 'var(--brand-text-muted, #8BA4B8)',
              padding: 4,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}
